import {$, $$} from '@vue-macros/reactivity-transform/macros'

import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'

import {
  IListPropertyTypeInterfaces,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/ListPropertyTypeStrategy/types/IListPropertyTypeInterfaces'
import {useLabelApiStore} from '@toolify/client/src/stores/api/LabelApiStore/useLabelApiStore'
import {useParticularColors} from '@toolify/client/src/modules/color/composables/useParticularColors'
import {ILabelInstance} from '@toolify/server/src/models/mongoose/LabelModel/types/ILabelInstance'
import {FilterItemType} from '@toolify/server/src/services/FilterService/enum/FilterItemType'
import {FilterPropertyOperator} from '@toolify/server/src/services/FilterService/enum/FilterPropertyOperator'
import {FilterPropertyPayloadType} from '@toolify/server/src/services/FilterService/enum/FilterPropertyPayloadType'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {usePropertyTypeStrategy} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/usePropertyTypeStrategy'
import {FilterGroupOperator} from '@toolify/server/src/services/FilterService/enum/FilterGroupOperator'
import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'
import {IFilterPropertyItem} from '@toolify/server/src/services/FilterService/types/IFilterPropertyItem'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {GroupSortModeType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupSortModeType'
import {COMMON_GROUP_SORT_LABEL_KEYS} from '@toolify/client/src/modules/databases/utils/groupSortModes'
import {compareByPosition} from '@toolify/server/src/helpers/compareByPosition'
import {ListSortType} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/ListPropertyTypeStrategy/enums/ListSortType'
import {compareLabels} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/ListPropertyTypeStrategy/functions/compareLabels'

export function useListValueTypeStrategy() {
  const labelApiStore = useLabelApiStore()
  const {getColorByParticularColor} = $(useParticularColors())
  const strategyComponentType = PropertyStrategyComponentType.Dropdown

  const fullValueToText = (value: IListPropertyTypeInterfaces['fullValue'], property: IDatabasePropertyResponseInstance) => {
    if(!value) {
      return ''
    }
    return value.map(value => value.title).join(', ')
  }

  const fullValueToColor = (value: IListPropertyTypeInterfaces['fullValue'], property: IDatabasePropertyResponseInstance):{color: string, colorKey:string | null} | null => {
    if(!value || value.length === 0) {
      return null
    }
    const colorKey = property ?  `${property.id}-${value.map(v => v.id || v.title || '').join('-')}` : null
    const particularColor = value.map(value => value.color).find(color => color)
    return {
      color: getColorByParticularColor(particularColor),
      colorKey,
    }
  }

  const getAvailableValues = async (property: IDatabasePropertyResponseInstance) => {
    const propertyTypeStrategy = usePropertyTypeStrategy(property.type)
    const labels = await labelApiStore.getLabels(propertyTypeStrategy.getSourcePropertyId(property))
    return {
      values: labels,
      isGlobal: false,
    }
  }

  const modifyProperty = (property: IDatabasePropertyResponseInstance, context: { cardSize?: number, showFieldNames?: boolean }) => {
    const MARGINS_AND_PADDINGS_OF_TABLE_CARD = 50
    const cardTableSize = context.cardSize - MARGINS_AND_PADDINGS_OF_TABLE_CARD
    const GAP = 10
    const NAME_COLUMN_WIDTH_RATIO = 0.33
    const NAME_PROPERTY_ICON_SIZE = 50
    const namePropertyWidth = NAME_COLUMN_WIDTH_RATIO * (cardTableSize) + GAP
    const namePropertySize = context.showFieldNames ? namePropertyWidth : NAME_PROPERTY_ICON_SIZE
    return {
      ...property,
      width: cardTableSize - namePropertySize,
    }
  }

  const fullValueToInputValue = (value: IListPropertyTypeInterfaces['fullValue']) => {
    if(!Array.isArray(value) || value.length === 0) {
      return []
    }
    return value.map(item => item.id || item.title).filter((item): item is string => !!item)
  }
  
  const getPossibleValueFilter = (possibleValue: ILabelInstance, group: string) => {
    const filterValue = possibleValue.id || possibleValue.title || null
    return {
      type: FilterItemType.Property,
      id: group,
      operator: {
        type: FilterPropertyOperator.ArrayIncludes,
      },
      subProperty: null,
      payload: {
        type: FilterPropertyPayloadType.Static,
        value: [filterValue],
      },
      isDisabled: false,
    }
  }

  const sortGroupItems = (a: {fullValue: IListPropertyTypeInterfaces['fullValue']}, b: {fullValue: IListPropertyTypeInterfaces['fullValue']}): number => {
    const aFullValue = a.fullValue
    const bFullValue = b.fullValue
    
    if (!aFullValue || !Array.isArray(aFullValue) || aFullValue.length === 0) return 1
    if (!bFullValue || !Array.isArray(bFullValue) || bFullValue.length === 0) return -1

    return compareByPosition(aFullValue[0], bFullValue[0])
  }

  const compareItemValues = (
    valueA: IListPropertyTypeInterfaces['fullValue'],
    valueB: IListPropertyTypeInterfaces['fullValue'],
    sortDirection: '1' | '-1',
    property?: IDatabasePropertyResponseInstance,
  ): number => {
    const hasValueA = Array.isArray(valueA) && valueA.length > 0
    const hasValueB = Array.isArray(valueB) && valueB.length > 0

    if (!hasValueA && !hasValueB) return 0
    if (!hasValueA) return 1
    if (!hasValueB) return -1

    const sortType = (property?.valueTypeOptions as {sortType?: ListSortType} | undefined)?.sortType
    const comparison = compareLabels(valueA[0], valueB[0], sortType)

    return sortDirection === '1' ? comparison : -comparison
  }

  const updateValueBySingleValueGroupSwitch = (
    currentValue: IListPropertyTypeInterfaces['fullValue'],
    oldGroupValue: IListPropertyTypeInterfaces['fullValue'],
    newGroupValue: IListPropertyTypeInterfaces['fullValue'],
  ): string[] | null => {
    const oldValueKey = oldGroupValue?.[0]?.id || oldGroupValue?.[0]?.title || null
    const newValueKey = newGroupValue?.[0]?.id || newGroupValue?.[0]?.title || null
    const newValue = (currentValue || [])
      .map(e => e.id || e.title || null)
      .filter((e): e is string => !!e && e !== oldValueKey)
      .concat(newValueKey || null)
      .filter((e): e is string => !!e)
    return newValue.length ? newValue : null
  }

  const updateFullValueBySingleValueGroupSwitch = (
    currentValue: IListPropertyTypeInterfaces['fullValue'],
    oldGroupValue: IListPropertyTypeInterfaces['fullValue'],
    newGroupValue: IListPropertyTypeInterfaces['fullValue'],
  ): IListPropertyTypeInterfaces['fullValue'] | null => {
    const oldValueKey = oldGroupValue?.[0]?.id || oldGroupValue?.[0]?.title || null
    const newValue = (currentValue || [])
      .filter(e => (e.id || e.title || null) !== oldValueKey)
      .concat(newGroupValue?.[0] || null)
      .filter(e => !!e)
    return newValue.length ? newValue : null
  }

  const searchToFilterQuery = (search: string, databaseProperties: IDatabasePropertyResponseInstance[], databasePropertiesTableDataMap?: Map<string, unknown>): IFilter => {
    const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const searchRegex = new RegExp(escapedSearch, 'i')
    return {
      type: FilterItemType.Group,
      operator: FilterGroupOperator.Or,
      items: databaseProperties.map(databaseProperty => {
        const propertyTypeStrategy = usePropertyTypeStrategy(databaseProperty.type)
        const sourcePropertyId = propertyTypeStrategy.getSourcePropertyId(databaseProperty)
        let labelIds: string[] = []
        if(databasePropertiesTableDataMap?.has(sourcePropertyId)) {
          const labels = databasePropertiesTableDataMap.get(sourcePropertyId) as Array<{id: string, title?: string}>
          if(labels && Array.isArray(labels)) {
            labelIds = labels
              .filter(label => label.title && searchRegex.test(label.title))
              .map(label => label.id)
          }
        }
        if(!labelIds.length) {
          return null
        }
        return {
          type: FilterItemType.Property,
          id: databaseProperty.id,
          subProperty: null,
          operator: {
            type: FilterPropertyOperator.In,
          },
          payload: {
            type: FilterPropertyPayloadType.Static,
            value: labelIds,
          },
          isDisabled: false,
        } as IFilterPropertyItem
      }).filter((item): item is IFilterPropertyItem => !!item),
    }
  }

  const canGroupByView = (viewType: DatabaseViewType, _property: IDatabasePropertyResponseInstance) =>
    viewType === DatabaseViewType.Table || viewType === DatabaseViewType.Timeline || viewType === DatabaseViewType.Kanban

  const groupSort = {
    availableModes: [
      GroupSortModeType.Natural,
      GroupSortModeType.ValueAsc,
      GroupSortModeType.ValueDesc,
      GroupSortModeType.CountDesc,
      GroupSortModeType.CountAsc,
    ],
    defaultMode: GroupSortModeType.Natural,
    getLabelKey: (mode: GroupSortModeType): string => COMMON_GROUP_SORT_LABEL_KEYS[mode],
    compareGroupValues: (
      valueA: IListPropertyTypeInterfaces['fullValue'],
      valueB: IListPropertyTypeInterfaces['fullValue'],
      sortDirection: '1' | '-1',
    ): number => {
      const hasA = valueA && Array.isArray(valueA) && valueA.length > 0
      const hasB = valueB && Array.isArray(valueB) && valueB.length > 0
      if (!hasA && !hasB) return 0
      if (!hasA) return 1
      if (!hasB) return -1
      const textA = valueA.map(item => item.title || '').join(', ')
      const textB = valueB.map(item => item.title || '').join(', ')
      const comparison = textA.localeCompare(textB)
      return sortDirection === '1' ? comparison : -comparison
    },
  }

  return $$({
    fullValueToText,
    fullValueToColor,
    fullValueToInputValue,
    getAvailableValues,
    modifyProperty,
    getPossibleValueFilter,
    strategyComponentType,
    sortGroupItems,
    compareItemValues,
    updateValueBySingleValueGroupSwitch,
    updateFullValueBySingleValueGroupSwitch,
    searchToFilterQuery,
    canGroupByView,
    groupSort,
  })
}
